import { DEFAULT_EMPLOYEE_COLOR } from "@/lib/const";
import { Employee, EmployeeJobTitle, EmployeeRole, Shop } from "@/types/v2";
import {
  EmailDeliveryStatus,
  EmployeeEmailStatus,
} from "@/types/v2/EmailDeliveryLog";

/**
 *
 * @param employeeId
 * @param employeesData
 * @param byAuthId
 */
export function getEmployeeName(
  employeeId: string | null | undefined,
  employeesData: Employee[],
  byAuthId: boolean = false
) {
  if (!employeeId) return ""; // Check if employeeId is null or undefined

  const employee = byAuthId
    ? employeesData?.find((e) => e.shopHeroAuthUid === employeeId)
    : employeesData?.find((e) => e.id === employeeId);
  if (!employee) return "Unknown"; // Return "Unknown" if no matching employee is found

  return `${employee.firstName} ${employee.lastName}`; // Combine first and last names
}

/**
 * Picks which employee list the Employees table shows. Soft-deleted
 * employees are only ever shown to super admins who turned the toggle on;
 * the `isSuperAdmin` check here is the gate, not the page's toggle.
 */
export function getTableEmployees({
  employees,
  employeesIncludingDeleted,
  includeDeleted,
  isSuperAdmin,
}: {
  employees: Employee[] | undefined;
  employeesIncludingDeleted: Employee[] | undefined;
  includeDeleted: boolean;
  isSuperAdmin: boolean;
}): Employee[] | undefined {
  return includeDeleted && isSuperAdmin ? employeesIncludingDeleted : employees;
}

/**
 * A live employee other than `target` who has the same email (ignoring case
 * and surrounding whitespace), if any. Employees resolve their login by email,
 * so two live employees sharing one would share an auth account. `employees`
 * should be the live list (deleted employees already excluded).
 */
export function findLiveEmployeeWithEmail(
  employees: Employee[] | undefined,
  target: Pick<Employee, "id" | "email">
): Employee | undefined {
  const normalize = (email: string | undefined) =>
    (email ?? "").trim().toLowerCase();
  const wanted = normalize(target.email);
  if (!wanted) return undefined;
  return employees?.find(
    (e) => e.id !== target.id && normalize(e.email) === wanted
  );
}

const isIntegratorSource = (source: string | undefined) =>
  source === "tekmetric" || source === "shopware";

/**
 * Created by an integration sync (Tekmetric or Shopware).
 *
 * The sync converters set only `_source`, so the create path stamps
 * `_creationSource` as "server", and nothing else creates employees
 * server-side. `_source` alone can't be trusted: any edit or soft-delete from
 * the UI overwrites it with "client". `_creationSource` never changes, so
 * "server" is the durable marker; an integrator name in either field is also
 * accepted.
 */
export function isSyncedEmployee(
  e: Pick<Employee, "_creationSource" | "_source">
): boolean {
  return (
    e._creationSource === "server" ||
    isIntegratorSource(e._creationSource) ||
    isIntegratorSource(e._source)
  );
}

/** Created by hand in Shop Hero (`client`), or a legacy doc with no source. */
export function isManualEmployee(
  e: Pick<Employee, "_creationSource" | "_source">
): boolean {
  return !isSyncedEmployee(e);
}

/**
 * Synced employees a manual `source` employee can be merged into: every synced
 * employee except the source itself. Deleted ones come first, then last name,
 * then first name.
 */
export function getMergeTargets(
  employeesIncludingDeleted: Employee[] | undefined,
  source: Pick<Employee, "id">
): Employee[] {
  return (employeesIncludingDeleted ?? [])
    .filter((e) => e.id !== source.id && isSyncedEmployee(e))
    .sort((a, b) => {
      const deleted = Number(!!b._deleted) - Number(!!a._deleted);
      if (deleted !== 0) return deleted;
      return (
        (a.lastName ?? "").localeCompare(b.lastName ?? "") ||
        (a.firstName ?? "").localeCompare(b.firstName ?? "")
      );
    });
}

/**
 * The highest role (admin > office > technician > viewOnly) the shop's role
 * lists grant `uid`, or undefined when the uid is in none of them.
 */
export function getRoleFromShopLists(
  shop:
    | Pick<
        Shop,
        | "adminUserIds"
        | "officeUserIds"
        | "technicianUserIds"
        | "viewOnlyUserIds"
      >
    | undefined,
  uid: string | undefined
): EmployeeRole | undefined {
  if (!shop || !uid) return undefined;
  if (shop.adminUserIds?.includes(uid)) return "admin";
  if (shop.officeUserIds?.includes(uid)) return "office";
  if (shop.technicianUserIds?.includes(uid)) return "technician";
  if (shop.viewOnlyUserIds?.includes(uid)) return "viewOnly";
  return undefined;
}

/** Fields a merge copies onto the synced employee; absent keys are not written. */
export type MergeFieldChanges = {
  color?: string;
  shopHeroJobTitle?: EmployeeJobTitle;
  /** Set only by executeMerge when both employees share one login. */
  shopHeroAuthUid?: string;
  shopHeroRole?: EmployeeRole;
};

/**
 * What to copy from a manual employee onto the synced one. Color is copied when
 * the synced color is missing or the default and the manual one is a real
 * choice; the job title when the synced one is unset. Keys are omitted (never
 * `undefined`) because Firestore rejects undefined values.
 */
export function getMergeFieldChanges(
  manual: Employee,
  synced: Employee
): MergeFieldChanges {
  const changes: MergeFieldChanges = {};
  const isDefaultOrMissing = (color: string | undefined) =>
    !color || color.toUpperCase() === DEFAULT_EMPLOYEE_COLOR;
  if (isDefaultOrMissing(synced.color) && !isDefaultOrMissing(manual.color)) {
    changes.color = manual.color;
  }
  if (synced.shopHeroJobTitle == null && manual.shopHeroJobTitle != null) {
    changes.shopHeroJobTitle = manual.shopHeroJobTitle;
  }
  return changes;
}

type CachedAuthFields = {
  authAccountStatus: "inactive" | "active" | "doesNotExist";
  hasLoggedIn: boolean;
};

/**
 * The Employees table caches rows (an employee plus the auth status looked up
 * for their account) under a key built from employee ids, and keeps up to two
 * such lists per shop (toggle on/off), so a cached row can be minutes behind
 * the live store. Rebuild each row from the live employee and keep only the
 * two cached auth fields. Starting from the live doc (rather than spreading it
 * over the row) matters: a field removed with deleteField(), like
 * `_deletedBy` on restore, is absent from the live doc and would otherwise
 * survive from the stale row. Rows missing from the live list stay as cached.
 */
export function withLiveEmployeeData<T extends Employee & CachedAuthFields>(
  rows: T[],
  liveEmployees: Employee[] | undefined
): T[] {
  if (!liveEmployees) return rows;
  const liveById = new Map(liveEmployees.map((e) => [e.id, e]));
  return rows.map((row) => {
    const live = liveById.get(row.id);
    return live
      ? ({
          ...live,
          authAccountStatus: row.authAccountStatus,
          hasLoggedIn: row.hasLoggedIn,
        } as T)
      : row;
  });
}

/**
 *
 * @param employee
 */
export function formatEmployeeName(employee: Employee | undefined | null) {
  if (!employee) return "Unknown"; // Check if employee is null or undefined
  return `${employee.firstName} ${employee.lastName}`; // Combine first and last names
}

/**
 *
 * @param employeeId
 * @param employeesData
 */
export function getEmployeeInitials(
  employeeId: string | null | undefined,
  employeesData: Employee[]
) {
  if (!employeeId) return ""; // Check if employeeId is null or undefined

  const employee = employeesData?.find(
    (employee) => employee.id === employeeId
  );
  if (!employee) return ""; // Return an empty string if no matching employee is found

  const firstInitial = employee?.firstName?.[0] ?? ""; // Safely access firstName[0] or return an empty string
  const lastInitial = employee?.lastName?.[0] ?? ""; // Safely access lastName[0] or return an empty string

  return `${firstInitial}${lastInitial}`; // Combine initials
}

/**
 *
 * @param employeeId
 * @param employeesData
 */
export function getServiceAdvisorColor(
  employeeId: string | null | undefined,
  employeesData: Employee[]
) {
  const fixedColors = [
    "#001f3f", // Dark Blue
    "#006400", // Dark Green
    "#FF8C00", // Dark Orange
    "#4B0082", // Dark Purple
    "#800000", // Dark Maroon
    "#8B0000", // Dark Red
    "#2F4F4F", // Dark Gray
    "#D87093", // Dark Pink
    "#8B4513", // Dark Brown
    "#0C0C0C", // Almost Black
    // Additional colors, darker and less vibrant
    "#661616",
    "#663716",
    "#665716",
    "#667716",
    "#4c6616",
    "#3c6616",
    "#2c6616",
    "#165221",
    "#165241",
    "#165261",
    "#165282",
  ];

  if (!employeeId) return "#333"; // Default color for undefined/null employeeId

  const employee = employeesData?.find(
    (employee) => employee.id === employeeId
  );

  if (!employee) return "#333"; // Return default if employee not found

  // If employee has a custom color, use it
  if (employee.color) {
    return employee.color;
  }

  // Create a hash of the employee ID to get a consistent but unique index
  let hash = 0;
  for (let i = 0; i < employeeId.length; i++) {
    const char = employeeId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Use the hash to get a color index, ensuring it's positive and within bounds
  const colorIndex = Math.abs(hash) % fixedColors.length;
  return fixedColors[colorIndex];
}

export type EmployeeAccountFields = {
  authAccountStatus: "inactive" | "active" | "doesNotExist";
  shopHeroAuthUid?: string;
  /** null = explicitly cleared (Deactivate); undefined = legacy, shown as admin. */
  shopHeroRole?: Employee["shopHeroRole"];
  hasLoggedIn: boolean;
  latestActivationEmailStatus?: EmployeeEmailStatus;
};

export type EmployeeAccountDisplayState =
  | { kind: "doesNotExist" }
  | { kind: "inactive" }
  | { kind: "notAssociated" }
  | { kind: "pendingFirstLogin"; emailStatus: EmailDeliveryStatus }
  | { kind: "active" };

/**
 * Priority order matters: authAccountStatus is checked before
 * shopHeroAuthUid, which is checked before hasLoggedIn, so an employee
 * can only be "active" once every earlier gate has passed.
 */
export function getEmployeeAccountDisplayState(
  employee: EmployeeAccountFields
): EmployeeAccountDisplayState {
  if (employee.authAccountStatus === "doesNotExist") {
    return { kind: "doesNotExist" };
  }
  if (employee.authAccountStatus === "inactive") {
    return { kind: "inactive" };
  }
  // An enabled account whose role this shop cleared (Deactivate, then the
  // account was re-enabled from another shop) has no access here.
  if (!employee.shopHeroAuthUid || employee.shopHeroRole === null) {
    return { kind: "notAssociated" };
  }
  if (!employee.hasLoggedIn) {
    return {
      kind: "pendingFirstLogin",
      emailStatus: employee.latestActivationEmailStatus?.status ?? "pending",
    };
  }
  return { kind: "active" };
}
